import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react'

/**
 * N-box PIN keypad (ookey-browser/UnlockScreen の 6-box parity)。
 *
 *   - 各 box は 1 桁、numeric only
 *   - 入力で次の box に auto-advance
 *   - Backspace で空なら 1 つ戻る、文字ありなら自分を空に
 *   - ArrowLeft / ArrowRight で navigate
 *   - 任意の box への paste で先頭から N 桁を分配 (非数字は除外)
 *   - 全 box が埋まったら `onComplete` を呼ぶ
 *   - `length` は呼出側 (LockPage) が pinVaultService.getStatus().pinLength から渡す
 *
 * 外部から focus したい場合は ref.current.focusFirst() を使う (autoFocus 用)。
 */

export interface PinPadHandle {
  focusFirst: () => void
  clear: () => void
}

export interface PinPadProps {
  /** 入力 box 数 (4-6)。設定時の PIN 桁数に合わせる。 */
  length: number
  /** すべての box が埋まったときに 1 度だけ呼ばれる。 */
  onComplete: (pin: string) => void
  /** 入力中の値変更を親で track したい場合 (例: submit ボタンの disabled)。 */
  onChange?: (pin: string) => void
  disabled?: boolean
  /** 視覚的に「エラー」状態 (赤枠) を付ける。 */
  hasError?: boolean
  /** 数字 vs ●● の表示切替 (デフォルトは ●= masked)。 */
  visible?: boolean
  /** aria 用 group label。 */
  ariaLabel?: string
}

const isDigit = (s: string): boolean => /^[0-9]$/.test(s)

export const PinPad = forwardRef<PinPadHandle, PinPadProps>(function PinPad(
  { length, onComplete, onChange, disabled, hasError, visible, ariaLabel },
  ref,
): JSX.Element {
  const [digits, setDigits] = useState<string[]>(() => Array.from({ length }, () => ''))
  const refs = useRef<Array<HTMLInputElement | null>>([])

  // length が変わった場合に digits を resize
  useEffect(() => {
    setDigits(prev => {
      if (prev.length === length) return prev

      return Array.from({ length }, (_, i) => prev[i] ?? '')
    })
    refs.current = refs.current.slice(0, length)
  }, [length])

  // 完成 / 変更 callback
  const joined = digits.join('')

  // 親が onComplete / onChange を inline arrow で渡すと毎 render 新 identity に
  // なるため、callback ref に最新版を保持して effect の deps から除外する。
  // (review I-10: 旧実装は eslint-disable で deps を省略していたため、length が
  // 変わって joined.length === length が一時的に成立した時に古い closure を
  // 呼んでしまう潜在リスクがあった)
  const onCompleteRef = useRef(onComplete)
  const onChangeRef = useRef(onChange)

  useEffect(() => {
    onCompleteRef.current = onComplete
    onChangeRef.current = onChange
  })

  // 同じ joined で onComplete を二重発火させないためのガード。
  // joined が変化したタイミングでのみ fire (clear → 再入力 → 完成 で再度通る)。
  const lastFiredForRef = useRef<string>('')

  useEffect(() => {
    onChangeRef.current?.(joined)
    if (joined.length === length && lastFiredForRef.current !== joined) {
      lastFiredForRef.current = joined
      onCompleteRef.current(joined)
    } else if (joined.length !== length) {
      // 入力が抜けた / clear された → ガードもリセット
      lastFiredForRef.current = ''
    }
  }, [joined, length])

  useImperativeHandle(
    ref,
    () => ({
      focusFirst: (): void => refs.current[0]?.focus(),
      clear: (): void => {
        setDigits(Array.from({ length }, () => ''))
        refs.current[0]?.focus()
      },
    }),
    [length],
  )

  const setDigitAt = useCallback((idx: number, value: string): void => {
    setDigits(prev => {
      const next = [...prev]

      next[idx] = value

      return next
    })
  }, [])

  const focusAt = (idx: number): void => {
    const clamped = Math.max(0, Math.min(length - 1, idx))

    refs.current[clamped]?.focus()
    refs.current[clamped]?.select()
  }

  const onInput =
    (idx: number) =>
    (ev: ChangeEvent<HTMLInputElement>): void => {
      const raw = ev.target.value
      // ブラウザは autofill で複数桁を 1 box に入れることがある
      if (raw.length > 1) {
        const digitsOnly = raw.replace(/\D/g, '').slice(0, length - idx)
        const next = [...digits]
        for (let i = 0; i < digitsOnly.length; i++) {
          next[idx + i] = digitsOnly[i] ?? ''
        }
        setDigits(next)
        focusAt(Math.min(idx + digitsOnly.length, length - 1))

        return
      }

      if (raw === '' || isDigit(raw)) {
        setDigitAt(idx, raw)
        if (raw !== '' && idx < length - 1) {
          focusAt(idx + 1)
        }
      }
    }

  const onKeyDown =
    (idx: number) =>
    (ev: KeyboardEvent<HTMLInputElement>): void => {
      if (ev.key === 'Backspace') {
        if (digits[idx] === '') {
          ev.preventDefault()
          if (idx > 0) {
            setDigitAt(idx - 1, '')
            focusAt(idx - 1)
          }
        }

        return
      }
      if (ev.key === 'ArrowLeft' && idx > 0) {
        ev.preventDefault()
        focusAt(idx - 1)

        return
      }
      if (ev.key === 'ArrowRight' && idx < length - 1) {
        ev.preventDefault()
        focusAt(idx + 1)
      }
    }

  const onPaste =
    (idx: number) =>
    (ev: ClipboardEvent<HTMLInputElement>): void => {
      const text = ev.clipboardData.getData('text').replace(/\D/g, '')
      if (text === '') return
      ev.preventDefault()

      const slice = text.slice(0, length - idx)
      const next = [...digits]
      for (let i = 0; i < slice.length; i++) {
        next[idx + i] = slice[i] ?? ''
      }
      setDigits(next)
      focusAt(Math.min(idx + slice.length, length - 1))
    }

  return (
    <div
      className={`pin-pad${hasError ? ' has-error' : ''}`}
      role="group"
      aria-label={ariaLabel ?? 'PIN'}
    >
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={el => {
            refs.current[i] = el
          }}
          type={visible ? 'text' : 'password'}
          inputMode="numeric"
          autoComplete="off"
          maxLength={1}
          className="pin-pad-box"
          value={digits[i] ?? ''}
          onChange={onInput(i)}
          onKeyDown={onKeyDown(i)}
          onPaste={onPaste(i)}
          disabled={disabled}
          aria-label={`PIN digit ${i + 1}`}
        />
      ))}
    </div>
  )
})
